import { sheetsEndpoint } from '../data/integrations';
import { Lead } from '../types/lead';
import { getAttribution } from './attribution';

/** Fila plana que recibe el Apps Script (acción okm.lead). */
export type LeadPayload = Record<string, string | number>;

const OUTBOX_KEY = 'vcars-lead-outbox-v1';
const yesNo = (b: boolean) => (b ? 'SI' : 'NO');

export function toLeadPayload(lead: Lead): LeadPayload {
  const a = getAttribution();
  const s = lead.simulation;
  return {
    codigo_publico: lead.code,
    consulta_id: lead.id,
    fecha: lead.createdAt,
    canal: lead.channel,
    nombre: lead.name,
    telefono: lead.phone,
    email: lead.email,
    preferencia_contacto: lead.contactPref,
    vehiculo_id: lead.vehicleId ?? '',
    vehiculo: lead.vehicleLabel ?? '',
    precio: lead.price ?? '',
    anticipo: lead.down ?? '',
    plazo: lead.term ?? '',
    cuota: s ? Math.round(s.installment) : '',
    tna: s ? s.tna : '',
    cft: s && s.cft !== null ? s.cft : '',
    costo_total: s ? Math.round(s.totalCost) : '',
    condicion: s ? (s.verified ? 'Oferta verificada' : 'Simulación referencial') : '',
    simulacion_json: s ? JSON.stringify(s) : '',
    mensaje: lead.message,
    banco: lead.bank ?? '',
    cuil: lead.cuil ?? '',
    usado: lead.tradeIn ? `${lead.tradeIn.brand} ${lead.tradeIn.model} ${lead.tradeIn.year} · ${lead.tradeIn.km} km` : '',
    usado_json: lead.tradeIn ? JSON.stringify(lead.tradeIn) : '',
    chequeo_anticipo: lead.credit?.downRange ?? '',
    chequeo_empleo: lead.credit?.employment ?? '',
    chequeo_antiguedad: lead.credit?.seniority ?? '',
    utm_source: a.utm_source,
    utm_medium: a.utm_medium,
    utm_campaign: a.utm_campaign,
    pagina: window.location.pathname,
    consentimiento_datos: yesNo(lead.consents.dataProcessing),
    consentimiento_contacto: yesNo(lead.consents.contact),
    fecha_consentimiento: lead.consents.at,
  };
}

function readOutbox(): LeadPayload[] {
  try {
    const raw = localStorage.getItem(OUTBOX_KEY);
    return raw ? (JSON.parse(raw) as LeadPayload[]) : [];
  } catch {
    return [];
  }
}

function writeOutbox(items: LeadPayload[]): void {
  try {
    localStorage.setItem(OUTBOX_KEY, JSON.stringify(items));
  } catch {
    // sin almacenamiento: el envío se intenta una sola vez
  }
}

/** true = quitar de la cola (guardada, duplicada o inválida). false = reintentar más tarde. */
async function post(payload: LeadPayload): Promise<boolean> {
  try {
    const res = await fetch(sheetsEndpoint, {
      method: 'POST',
      // text/plain evita la verificación previa (CORS) que Apps Script no admite.
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify({ action: 'okm.lead', lead: payload }),
      redirect: 'follow',
    });
    const json = (await res.json()) as { ok?: boolean; code?: string };
    if (json.ok) return true;
    return json.code === 'INVALID' || json.code === 'DUPLICATE';
  } catch {
    return false;
  }
}

let flushing = false;

/** Envía las consultas pendientes, en orden. Si falla la conexión, quedan guardadas para reintentar. */
export async function flushLeadOutbox(): Promise<void> {
  if (flushing) return;
  flushing = true;
  try {
    for (const item of readOutbox()) {
      const done = await post(item);
      if (!done) break;
      writeOutbox(readOutbox().filter((x) => x.codigo_publico !== item.codigo_publico));
    }
  } finally {
    flushing = false;
  }
}

export function enqueueLead(lead: Lead): void {
  const payload = toLeadPayload(lead);
  writeOutbox([...readOutbox().filter((x) => x.codigo_publico !== payload.codigo_publico), payload]);
  void flushLeadOutbox();
}

export function pendingLeadCount(): number {
  return readOutbox().length;
}
